import { createApi } from '@reduxjs/toolkit/query/react';
import { storeSavedSearch } from './helpers/saved-searches-storage';
import { sortSavedSearchesResponseOnLatestTotalCount } from './helpers/search-mapper';
import qs from 'qs';
import type {
    BlueshiftRecommendations4ApiResponse,
    GetFavouriteSellersAndItems2ApiResponse,
    GetSellerSuggestionsApiResponse,
    ExecuteSavedSearchApiResponse,
    ExecuteSavedSearchForCurrentUserApiRequest,
    GetSavedSearchesApiResponse,
    FavouritesellersAndItems2Request,
    RecommendationsV4Request,
    SellerSuggestionsRequest,
    GetTrendingSellersAndItemsApiResponse,
    TrendingsellersAndItemsRequest,
    BlueshiftSellpyRecommendationsApiResponse,
    SellpyRequest,
    ItemsByIdsApiResponse,
    ItemsItemsByIdsRequest,
    IndependentSearchApiRequest,
    IndependentSearchApiResponse,
    CategoryPageApiRequest,
    CategoryPageApiResponse,
    LastChanceApiResponse,
    StartPageLastChanceRequest,
    BrandSuggestionsApiResponse,
    IdeasApiResponse,
    TrendingSearchesApiResponse,
    TrendingSearchViewModel,
    WishlistApiApiResponse
} from './types/webapi-discover-generated';
import { isServer } from 'tradera/utils/nextjs';
import { baseQueryForWishListableItems } from './utils/webapi-base-query';

export type RecommendationsQueryParams = {
    slot: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
    shippingCountryCode: string;
    maxResults: number;
    pageNumber: number;
};

export type SuggestedRecommendationsQueryParams = {
    slot: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
    shippingCountryCode: string;
};

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getRecommendationsQueryParams = ({
    slot,
    userLanguage,
    automaticTranslationPreferred,
    shippingCountryCode
}: SuggestedRecommendationsQueryParams): RecommendationsQueryParams => ({
    slot,
    maxResults: 20,
    userLanguage: userLanguage.toLowerCase(),
    automaticTranslationPreferred,
    shippingCountryCode: shippingCountryCode.toUpperCase(),
    pageNumber: 0
});

type FavouriteSellersQueryParams = {
    shippingCountryCode: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
};

export type LastChanceQueryParams = {
    automaticTranslationPreferred: boolean;
    itemCount: number;
    shippingCountryCode: string;
    userLanguage: string;
};

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getFavouriteSellersQueryParams = ({
    shippingCountryCode,
    userLanguage,
    automaticTranslationPreferred
}: FavouriteSellersQueryParams): FavouriteSellersQueryParams => ({
    shippingCountryCode: shippingCountryCode.toUpperCase(),
    userLanguage: userLanguage.toLowerCase(),
    automaticTranslationPreferred
});

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getSellerSuggestionsQueryParams = (
    shippingCountryCode: string
) => ({
    shippingCountryCode: shippingCountryCode.toUpperCase()
});

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getTrendingSellersQueryParams = (
    shippingCountryCode: string,
    userLanguage: string
) => ({
    shippingCountryCode: shippingCountryCode.toUpperCase(),
    userLanguage: userLanguage.toLowerCase()
});

enum SavedSearchesSorting {
    Sorted = 'sorted',
    Unsorted = 'unsorted'
}

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching.
 */
export const getSavedSearchesQueryParams = (isSortingRequired: boolean) =>
    isSortingRequired
        ? SavedSearchesSorting.Sorted
        : SavedSearchesSorting.Unsorted;

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getInstagramQueryParams = () => ({});

export const getLastChanceQueryParams = (
    userLanguage: string,
    shippingCountryCode: string,
    automaticTranslationPreferred: boolean
): LastChanceQueryParams => ({
    itemCount: 19,
    userLanguage: userLanguage.toLowerCase(),
    shippingCountryCode: shippingCountryCode.toUpperCase(),
    automaticTranslationPreferred
});

export const discoverApi = createApi({
    reducerPath: 'discoveryApi',
    baseQuery: baseQueryForWishListableItems,
    endpoints: (builder) => ({
        getBrandSuggestions: builder.query<
            BrandSuggestionsApiResponse,
            unknown
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/brand-suggestions`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getFavouriteSellers: builder.query<
            GetFavouriteSellersAndItems2ApiResponse,
            FavouritesellersAndItems2Request
        >({
            query: ({
                shippingCountryCode,
                userLanguage,
                automaticTranslationPreferred,
                maxItemsPerFavouriteSeller = 3,
                maxItems = 20,
                maxFavouriteSellers = 20
            }) => {
                const query = qs.stringify({
                    shippingCountryCode,
                    userLanguage,
                    automaticTranslationPreferred,
                    maxItemsPerFavouriteSeller,
                    maxItems,
                    maxFavouriteSellers
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/favouritesellers-and-items2?${query}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getIdeas: builder.query<IdeasApiResponse, unknown>({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/ideas`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getInstagramFeed: builder.query<
            GetFavouriteSellersAndItems2ApiResponse,
            FavouritesellersAndItems2Request
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/instagram-feed`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getLastChance: builder.query<
            LastChanceApiResponse,
            LastChanceQueryParams
        >({
            query: ({
                userLanguage,
                shippingCountryCode,
                automaticTranslationPreferred,
                itemCount
            }) => {
                const request: StartPageLastChanceRequest = {
                    shippingCountryCodeIso2: shippingCountryCode,
                    languageCodeIso2: userLanguage,
                    automaticTranslationPreferred:
                        automaticTranslationPreferred,
                    limit: itemCount
                };
                const query = qs.stringify(request);
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/last-chance?${query}`;
            },
            extraOptions: {
                getWishListableItems: (response: LastChanceApiResponse) =>
                    response.items
            }
        }),
        getSellerSuggestions: builder.query<
            GetSellerSuggestionsApiResponse,
            SellerSuggestionsRequest
        >({
            query: ({ shippingCountryCode, maxSellers = 20 }) => {
                const query = qs.stringify({
                    shippingCountryCode,
                    maxSellers
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/seller-suggestions?${query}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getTrendingSellers: builder.query<
            GetTrendingSellersAndItemsApiResponse,
            TrendingsellersAndItemsRequest
        >({
            query: ({
                maxItems = 0,
                maxItemsPerSeller = 3,
                maxSellers = 20,
                ...props
            }) => {
                const query = qs.stringify({
                    maxItems,
                    maxItemsPerSeller,
                    maxSellers,
                    ...props
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/trendingsellers-and-items?${query}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getTrendingSearches: builder.query<
            TrendingSearchViewModel[],
            undefined
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/trending-searches`;
            },
            extraOptions: {
                getWishListableItems: () => null
            },
            transformResponse: (response: TrendingSearchesApiResponse) => {
                return response.searches || [];
            }
        }),
        getSavedSearches: builder.query<GetSavedSearchesApiResponse, unknown>({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/savedsearches`;
            },
            extraOptions: {
                getWishListableItems: () => null
            },
            transformResponse: (response, _meta, arg) => {
                return arg === SavedSearchesSorting.Unsorted
                    ? response
                    : sortSavedSearchesResponseOnLatestTotalCount(response);
            }
        }),
        getRecommendations: builder.query<
            BlueshiftRecommendations4ApiResponse,
            RecommendationsV4Request
        >({
            query: ({
                slot,
                shippingCountryCode,
                userLanguage,
                automaticTranslationPreferred,
                maxResults,
                pageNumber
            }: RecommendationsQueryParams) => {
                const query = qs.stringify({
                    slot,
                    pageNumber,
                    shippingCountryCode,
                    userLanguage,
                    automaticTranslationPreferred,
                    maxResults
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/recommendations-v4?${query}`;
            },
            extraOptions: {
                getWishListableItems: (
                    response: BlueshiftRecommendations4ApiResponse
                ) => response.items
            }
        }),
        getIndependentSearchResult: builder.query<
            IndependentSearchApiResponse,
            IndependentSearchApiRequest
        >({
            query: (parameters) => {
                return {
                    url: '/api/webapi/discover/web/independent-search',
                    method: 'POST',
                    body: parameters
                };
            },
            extraOptions: {
                getWishListableItems: (
                    response: IndependentSearchApiResponse
                ) => response.items
            }
        }),

        getCategoryResult: builder.query<
            CategoryPageApiResponse,
            CategoryPageApiRequest
        >({
            query: (parameters) => {
                return {
                    url: '/api/webapi/discover/web/category-page',
                    method: 'POST',
                    body: parameters
                };
            },
            extraOptions: {
                getWishListableItems: (response: CategoryPageApiResponse) =>
                    response.items
            }
        }),
        getSavedSearchResults: builder.mutation<
            ExecuteSavedSearchApiResponse,
            ExecuteSavedSearchForCurrentUserApiRequest
        >({
            query: (parameters) => {
                return {
                    url: '/api/webapi/discover/savedsearches/search-v2',
                    method: 'POST',
                    body: parameters
                };
            },
            extraOptions: {
                //response.response?.result?.items are of type SearchResultItem2ApiDto which does not currently have the "inWishList" property
                getWishListableItems: () => null
            },
            transformResponse: (
                response: ExecuteSavedSearchApiResponse,
                meta,
                arg
            ) => {
                storeSavedSearch(arg.id, {
                    totalItems: response.response?.result?.itemsMatched
                });
                return response;
            }
        }),
        getSellpyRecommendations: builder.query<
            BlueshiftSellpyRecommendationsApiResponse,
            SellpyRequest
        >({
            query: (params) => {
                const query = qs.stringify({
                    ...params,
                    maxResults: params.maxResults ?? 50
                });

                return `/api/webapi/discover/recommendations/sellpy?${query}`;
            },
            extraOptions: {
                getWishListableItems: (
                    response: BlueshiftSellpyRecommendationsApiResponse
                ) => response.items
            }
        }),
        getItemsByIds: builder.query<
            ItemsByIdsApiResponse,
            ItemsItemsByIdsRequest
        >({
            query: (params) => {
                const query = qs.stringify(params);
                return `/api/webapi/discover/web/items/items-by-ids?${query}`;
            },
            extraOptions: {
                getWishListableItems: (response: ItemsByIdsApiResponse) =>
                    response.items
            }
        }),
        getStartPageWishListItems: builder.query<
            WishlistApiApiResponse,
            undefined
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/wishlist?limit=100`;
            },
            extraOptions: {
                getWishListableItems: (data: WishlistApiApiResponse) =>
                    data.items
            }
        })
    })
});

export const {
    useGetBrandSuggestionsQuery,
    useGetIdeasQuery,
    useGetInstagramFeedQuery,
    useGetIndependentSearchResultQuery,
    useGetCategoryResultQuery,
    useGetLastChanceQuery,
    useGetSavedSearchesQuery,
    useGetSavedSearchResultsMutation,
    useGetRecommendationsQuery,
    useGetFavouriteSellersQuery,
    useGetTrendingSearchesQuery,
    useGetSellpyRecommendationsQuery,
    useGetItemsByIdsQuery,
    useGetStartPageWishListItemsQuery,
    endpoints
} = discoverApi;

export const { updateQueryData } = discoverApi.util;
